'use client';
import { shared } from '../config';

/**
 * Elevar-only reporter for dl_* events.
 */
export class ElevarReporter {
  static get _enabled() {
    return shared.OLLIE_ENV !== 'local';
  }

  static get _elevarUserProperties() {
    if (!this.__elevarUserProperties) {
      this.__elevarUserProperties = { visitor_type: 'guest' };
    }

    return this.__elevarUserProperties;
  }

  static set _elevarUserProperties(value) {
    this.__elevarUserProperties = value;
  }

  static get _elevarCartSummary() {
    if (!this.__elevarCartSummary) {
      this.__elevarCartSummary = {
        cart_total: '0.00',
        products: [],
      };
    }

    return this.__elevarCartSummary;
  }

  static set _elevarCartSummary(value) {
    this.__elevarCartSummary = {
      ...ElevarReporter._elevarCartSummary,
      ...value,
    };
  }

  constructor({ prefix = '' } = {}) {
    this.prefix = prefix;
  }

  withPrefix(prefix) {
    return new ElevarReporter({ prefix });
  }

  updateElevarCartSummary({ cartTotal } = {}) {
    const normalizedCartTotal =
      cartTotal !== undefined && cartTotal !== null
        ? this._formatElevarPrice(cartTotal)
        : ElevarReporter._elevarCartSummary.cart_total;

    ElevarReporter._elevarCartSummary = {
      cart_total: normalizedCartTotal,
      products: ElevarReporter._elevarCartSummary.products,
    };
  }

  userData({
    page_title,
    userProperties = {},
    cartSummary = {},
    ...rest
  } = {}) {
    const baseData = this._buildElevarBaseEventData(cartSummary);
    const userProps = this._buildElevarUserProperties(userProperties);

    this._pushToElevar({
      event: 'dl_user_data',
      page_title,
      user_properties: userProps,
      ...rest,
      ...baseData,
    });
  }

  elevarViewItem({ planType, userProperties = {} }) {
    if (!planType) return;

    const list = this._getElevarList();
    this._pushToElevar({
      event: 'dl_view_item',
      user_properties: this._buildElevarUserProperties(userProperties),
      ecommerce: {
        currencyCode: 'USD',
        detail: {
          actionField: {
            list,
            action: 'detail',
          },
          products: [
            this._formatElevarProduct(planType, {
              list,
              quantity: '1',
            }),
          ],
        },
      },
    });
  }

  elevarAddToCart({ product, userProperties = {}, quantity = '1' }) {
    if (!product) return;

    const list = this._getElevarList();
    this._pushToElevar({
      event: 'dl_add_to_cart',
      user_properties: this._buildElevarUserProperties(userProperties),
      ecommerce: {
        currencyCode: 'USD',
        add: {
          actionField: {
            list,
          },
          products: [
            this._formatElevarProduct(product, {
              list,
              quantity,
              url: this._getElevarProductUrl(),
            }),
          ],
        },
      },
    });
  }

  elevarRemoveFromCart({ product, userProperties = {}, quantity = '1' }) {
    if (!product) return;

    const list = this._getElevarList();
    this._pushToElevar({
      event: 'dl_remove_from_cart',
      user_properties: this._buildElevarUserProperties(userProperties),
      ecommerce: {
        currencyCode: 'USD',
        remove: {
          actionField: {
            list,
          },
          products: [
            this._formatElevarProduct(product, {
              list,
              quantity,
              url: this._getElevarProductUrl(),
            }),
          ],
        },
      },
    });
  }

  elevarViewItemList({ products = [], userProperties = {}, listName }) {
    if (!products.length) return;

    const list = this._getElevarList(listName);
    const impressions = products.map((product, index) =>
      this._formatElevarProduct(product, {
        list,
        position: index + 1,
      }),
    );

    this._pushToElevar({
      event: 'dl_view_item_list',
      user_properties: this._buildElevarUserProperties(userProperties),
      ecommerce: {
        currencyCode: 'USD',
        impressions,
      },
    });
  }

  elevarSelectItem({ product, position, userProperties = {}, listName }) {
    if (!product) return;

    const list = this._getElevarList(listName);
    this._pushToElevar({
      event: 'dl_select_item',
      user_properties: this._buildElevarUserProperties(userProperties),
      ecommerce: {
        currencyCode: 'USD',
        click: {
          actionField: {
            list,
            action: 'click',
          },
          products: [
            this._formatElevarProduct(product, {
              list,
              position,
            }),
          ],
        },
      },
    });
  }

  elevarSubscribe({ email, phone, leadType, userProperties = {} }) {
    if (!email && !phone) return;

    const resolvedLeadType = leadType || (phone && !email ? 'phone' : 'email');

    this._pushToElevar({
      event: 'dl_subscribe',
      lead_type: resolvedLeadType,
      user_properties: this._buildElevarUserProperties({
        ...userProperties,
        ...(email ? { customer_email: email } : {}),
        ...(phone ? { customer_phone: phone } : {}),
      }),
    });
  }

  elevarCompleteQuiz({ userProperties = {} } = {}) {
    this._pushToElevar({
      event: 'dl_complete_quiz',
      user_properties: this._buildElevarUserProperties(userProperties),
      event_properties: {
        quiz_result: 'unknown',
      },
    });
  }

  _pushToElevar(eventData) {
    if (typeof window !== 'undefined' && window.ElevarDataLayer) {
      try {
        if (eventData?.event === 'dl_user_data') {
          const baseData = this._buildElevarBaseEventData();
          eventData = this._mergeElevarBaseEventData(baseData, eventData);
        }

        window.ElevarDataLayer.push(eventData);
      } catch {
        // silently fail
      }
    }
  }

  _buildElevarUserProperties(userProperties = {}) {
    const sanitized = Object.fromEntries(
      Object.entries(userProperties).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const merged = {
      ...ElevarReporter._elevarUserProperties,
      ...sanitized,
    };

    if (!merged.visitor_type) {
      merged.visitor_type = merged.customer_id ? 'logged_in' : 'guest';
    }

    ElevarReporter._elevarUserProperties = merged;
    return merged;
  }

  _buildElevarBaseEventData(overrides = {}) {
    const summary = {
      ...ElevarReporter._elevarCartSummary,
      ...overrides,
    };

    const cartTotal =
      summary.cart_total !== undefined && summary.cart_total !== null
        ? `${summary.cart_total}`
        : '0.00';

    const products = Array.isArray(summary.products) ? summary.products : [];
    const currencyCode = 'USD';

    return {
      cart_total: cartTotal,
      ecommerce: {
        currencyCode,
        cart_contents: {
          products,
        },
      },
    };
  }

  _formatElevarProduct(product = {}, overrides = {}) {
    const baseProduct = product || {};
    const priceCandidate =
      overrides.price ??
      baseProduct.discountedPrice ??
      baseProduct.price ??
      baseProduct.pricingInfo?.price;

    const formattedProduct = {
      id:
        baseProduct.combination_id ||
        baseProduct.sku ||
        baseProduct.product_id ||
        baseProduct.value ||
        'unknown',
      name:
        baseProduct.productName ||
        baseProduct.title ||
        baseProduct.label ||
        baseProduct.name ||
        'Unknown Product',
      brand: baseProduct.brand || 'Ollie',
      category: baseProduct.productType || baseProduct.category || 'Meal Plan',
      variant:
        baseProduct.variant ||
        baseProduct.value ||
        baseProduct.sku ||
        'default',
      price: this._formatElevarPrice(priceCandidate),
      quantity:
        overrides.quantity ??
        (baseProduct.quantity
          ? String(baseProduct.quantity)
          : baseProduct.qty
          ? String(baseProduct.qty)
          : undefined),
      product_id:
        baseProduct.product_id ||
        baseProduct.sku ||
        baseProduct.value ||
        'unknown',
      variant_id: baseProduct.sku || baseProduct.value || 'unknown',
      sku: baseProduct.sku || baseProduct.value || 'unknown',
      compare_at_price:
        overrides.compare_at_price ||
        baseProduct.compare_at_price ||
        baseProduct.pricingInfo?.compareAtPrice ||
        baseProduct.compareAtPrice,
      image:
        this._sanitizeElevarUrl(
          overrides.image ||
            baseProduct.image ||
            (typeof window !== 'undefined'
              ? window.location.origin
              : undefined),
        ) ||
        (typeof window !== 'undefined'
          ? window.location.origin
          : 'https://example.com'),
      url: overrides.url,
      list: overrides.list,
      position: overrides.position,
    };

    const cleaned = Object.fromEntries(
      Object.entries(formattedProduct).filter(
        ([, value]) => value !== undefined,
      ),
    );

    if (cleaned.quantity !== undefined) {
      cleaned.quantity = String(cleaned.quantity);
    }

    cleaned.product_id = this._normalizeElevarId(
      cleaned.product_id ?? cleaned.sku ?? cleaned.id,
    );
    cleaned.variant_id = this._normalizeElevarId(
      cleaned.variant_id ?? cleaned.sku ?? cleaned.product_id,
    );

    return cleaned;
  }

  _mergeElevarBaseEventData(baseData, eventData) {
    const mergedEcommerce = {
      ...baseData.ecommerce,
      ...eventData.ecommerce,
      cart_contents: {
        ...(baseData.ecommerce?.cart_contents || {}),
        ...(eventData.ecommerce?.cart_contents || {}),
      },
    };

    return {
      ...baseData,
      ...eventData,
      cart_total: eventData.cart_total ?? baseData.cart_total,
      ecommerce: mergedEcommerce,
    };
  }

  _formatElevarPrice(value) {
    if (value === undefined || value === null) {
      return '0.00';
    }

    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return `${value}`;
  }

  _getElevarList(listOverride) {
    if (typeof listOverride === 'string') {
      return listOverride;
    }

    return typeof window !== 'undefined' ? window.location.pathname : '';
  }

  _getElevarProductUrl() {
    return typeof window !== 'undefined' ? window.location.href : undefined;
  }

  _normalizeElevarId(value) {
    if (value === undefined || value === null) return undefined;
    const asString = `${value}`;
    return /^\d+$/.test(asString)
      ? asString
      : `${Math.abs(this._simpleHash(asString))}`;
  }

  _simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  }

  _sanitizeElevarUrl(value) {
    if (!value) return undefined;
    try {
      const url = new URL(value, window.location.href);
      return url.toString();
    } catch (_e) {
      return undefined;
    }
  }
}
